import type { LibraryFolderEntry } from '@lib/library/folderPath';
import type { Media } from '@typings/Media';

/**
 * Minimal typed event bus. One EventBus instance per event name, one payload
 * shape per bus. Subscribers get a disposer back; stores with a ReactionManager
 * can push that disposer into `_reactions` so it fires on deinit.
 *
 * Not for high-frequency streams — designed for infrequent fan-out between
 * stores that don't share a React component subtree (e.g. "upload manager
 * deleted this media, library folder should drop it").
 */
export type EventBusListener<T> = (payload: T) => void;

export class EventBus<T> {
  private listeners = new Set<EventBusListener<T>>();

  on(listener: EventBusListener<T>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(payload: T): void {
    // Snapshot so a listener unsubscribing mid-emit doesn't skip siblings.
    for (const listener of [...this.listeners]) {
      try {
        listener(payload);
      } catch (e) {
        console.warn('EventBus listener threw:', e);
      }
    }
  }
}

/**
 * Fired when UploadManager has successfully deleted media on the backend.
 * LibraryFolderStore listens and prunes its local `data.files` when the
 * libraryId matches — so cancel-batch from the panel is reflected in the
 * library grid without waiting for the status-poll cycle to notice a 404.
 */
export const mediaDeletedEvent = new EventBus<{
  libraryId: string;
  mediaIds: string[];
}>();

/**
 * Fired by `MediaCommentsStore` once the backend has added or removed a top-level comment.
 * LibraryFolderStore listens and shifts that media's `commentCount` by `delta`, so the card
 * stays right after you leave the modal, without waiting for the listing to be fetched again.
 */
export const mediaCommentCountChangedEvent = new EventBus<{
  libraryId: string;
  mediaId: string;
  delta: 1 | -1;
}>();

/**
 * Fired by `MediaStatusPoller` when a folder listing shows media whose state
 * has moved. LibraryFolderStore listens and patches matching entries in its
 * local `data.files`, so the library reflects the new status without waiting
 * for its own fetch.
 *
 * Subscribers should only patch entries that already exist in their local
 * state, not add new ones.
 *
 * ## `media` is a DELTA, not a snapshot
 *
 * It carries **only the records that changed** since the last emission — never
 * the full folder. So an id being absent from `media` means *unchanged*, and
 * almost never *gone*.
 *
 * Any consumer reasoning about a whole set — "are all of these done?", "which
 * of these errored?" — must therefore ask `mediaStatusPoller.getSnapshot()`
 * instead of inferring from this payload. Reading absence as absence here made
 * batch cards never reach 100%, wiped recorded failures, and issued a
 * per-media confirm request for every healthy media on every tick.
 */
export const mediaUpdatedEvent = new EventBus<{
  libraryId: string;
  media: Media[];
}>();

/**
 * Fired with a library's whole folder list every time the backend answers a folder mutation —
 * each of those routes returns the tree as it now stands. `LibraryFolderStore` takes its folder
 * list from here, so a folder the upload pipeline creates for a dropped directory shows up in
 * the open library exactly like one the user created from the page.
 */
export const libraryFoldersChangedEvent = new EventBus<{
  libraryId: string;
  folders: LibraryFolderEntry[];
}>();
